import { PageTitle } from 'components/PageTitle'
import { BreakoutButtonLink } from 'components/design-system/BreakoutButton'
import { useRootStore } from 'hooks/rootStore'
import { useQueryParams } from 'hooks/router'
import { useLTIMode } from 'hooks/use-lti-mode'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import { Trans } from 'react-i18next'
import { buildSupportMailto } from './ltiErrorMailto'

export const LtiErrorPage = observer(function LtiErrorPage() {
  const params = useQueryParams()
  const rootStore = useRootStore()

  const errorText = ((params ? params['text'] : '') || '').toString()

  return (
    <LtiErrorView
      errorText={errorText}
      userEmail={rootStore.ltiErrorUserEmail}
    />
  )
})

/**
 * `userEmail` is only known when /login/token signed the launching user in
 * and then refused them. On every other path (sign-in failed, server
 * rejected the launch) it stays undefined and the support email says so.
 */
export const LtiErrorView = observer(function LtiErrorView({
  errorText,
  userEmail,
}: {
  errorText: string
  userEmail?: string
}) {
  const { tt } = useTranslationTyped()
  const lines = errorText ? errorText.split('\\n') : []
  const { ltiMode, ltiOptions } = useLTIMode()

  const mailto = buildSupportMailto({
    userEmail,
    ltiMode,
    sectionId: ltiOptions?.sectionId,
    organizationId: ltiOptions?.organizationId,
    placement: ltiOptions?.placement,
    errorLines: lines,
  })

  return (
    <main
      data-testid="lti-error-page"
      className="lti-configuration h-full w-full"
    >
      <PageTitle title={tt.lti.page_title()} />
      <div className="">
        <img
          src="/assets/images/logo.png"
          width={64}
          className="absolute top-8 right-8"
          alt={tt.breakout_learning()}
        />
      </div>
      <div className="lti-error flex min-h-full w-full flex-col items-start justify-center">
        <div className="ml-[5vw] max-w-[550px]">
          <div className="mb-5 flex flex-row">
            <div className="max-w-[400px] pr-5">
              <h1
                className="mb-3 text-headline-large"
                data-testid="lti-error-header"
              >
                {tt.lti.configuration.error_header()}
              </h1>
              <p className="text-body-large">
                {tt.lti.configuration.error_subheader()}
              </p>
              <ol
                className="my-4 flex list-decimal flex-col gap-y-2 pl-5 text-body-large"
                data-testid="lti-error-steps"
              >
                <li>{tt.lti.configuration.error_step_close()}</li>
                <li>{tt.lti.configuration.error_step_relaunch()}</li>
                <li>
                  <Trans
                    i18nKey="lti.configuration.error_step_support"
                    components={{ b: <strong /> }}
                  />
                </li>
              </ol>
              {lines.length > 0 && (
                <div className="my-4 rounded-2xl bg-core-tertiary p-4 text-label-large">
                  <ul data-testid="lti-error-lines" className="list-disc pl-5">
                    {lines.map((line, index) => (
                      <li key={index}>{line}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <BreakoutButtonLink
                  target="_top"
                  href={mailto}
                  kind="accent"
                  size="medium"
                  className="py-2!"
                  testId="lti-error-contact-support"
                >
                  {tt.lti.configuration.error_contact_support()}
                </BreakoutButtonLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  )
})
